/**
 * Shared progress heuristics for the two-language survey progress system.
 *
 * Two distinct indicators, never on screen at once:
 *  - The OVERALL stepper (segmented) shows on interstitial nodes between
 *    sections (orientation, transition, intake). Its segments are the
 *    member-facing SECTION nodes — form sections and sensagrams. See
 *    {@link deriveSurveySegments}.
 *  - The SECTION line (a single continuous hairline) shows INSIDE a section.
 *    Its fraction is computed per surface but always normalized to 0..1:
 *      - sequential form: (index + 1) / totalFields (computed at the callsite)
 *      - clustered form:  required answered / required total — {@link clusteredCompletion}
 *      - sensagram:       completed / estimated total, indeterminate when
 *                         the total is unknown — {@link sensagramSectionProgress}
 *
 * These are pure functions so the rendering components stay dumb and the logic
 * is testable in isolation.
 */

import type { RuntimeExperienceNodeV2 } from '../types/runtime-experience-node-v2';
import type { FieldType, RenderableFormSectionSchema } from '../types/form-section';
import type { SensagramWrapCriteria } from '../types/sensagram-version';

/**
 * Node types that count as member-facing "sections" in the overall stepper.
 * Orientation / entry_intake / transition / sensacore_decision are the
 * connective tissue between sections, not sections themselves.
 */
const SECTION_NODE_TYPES: ReadonlySet<RuntimeExperienceNodeV2['node_type']> =
  new Set(['form_section', 'sensagram_attachment']);

/**
 * Whether a runtime node is a member-facing "section" (form section or
 * sensagram) — the units the overall stepper / journey rail counts.
 * Interstitials (orientation / entry_intake / transition / sensacore_decision)
 * are connective tissue, not sections, so they are not counted.
 */
export function isSectionNode(
  node: Pick<RuntimeExperienceNodeV2, 'node_type'> | null | undefined,
): boolean {
  return node != null && SECTION_NODE_TYPES.has(node.node_type);
}

export type SurveySegmentStatus = 'done' | 'current' | 'upcoming';

export interface SurveySegment {
  nodeKey: string;
  title: string | null;
  status: SurveySegmentStatus;
  /**
   * Rough estimated seconds to complete this section — drives the journey rail's
   * bar width so each chunk is sized to the time it takes, not split evenly.
   * Always set by {@link deriveSurveySegments}; optional so hand-built segments
   * (tests, ad-hoc callers) can omit it and the rail falls back to equal growth.
   * See {@link sectionWeightSeconds}.
   */
  weight?: number;
}

// ── Section time weighting (journey rail bar widths) ──────────────────────────
// Rough, relative effort estimates in "seconds". These are deliberately coarse
// — the rail only needs PROPORTIONS, not real durations — and live here as named
// constants so they are easy to tune. A field's cost is its base by type, plus
// a per-option reading cost for selects, plus a voice-probe allowance when it
// has inline prompts, discounted when it only shows conditionally.

/** Base seconds-to-answer by field type (a tap is cheap; open text is dear). */
const FIELD_BASE_SECONDS: Record<FieldType, number> = {
  yes_no: 6,
  single_select: 8,
  likert_segmented: 8,
  nps_slider: 8,
  slider: 8,
  number: 12,
  multi_select: 12,
  text: 24,
};
/** Seconds added per selectable option beyond the first two (reading cost). */
const SECONDS_PER_EXTRA_OPTION = 1;
/** A field with inline voice prompts invites a spoken follow-up. */
const INLINE_PROMPT_SECONDS = 18;
/** Conditionally-displayed fields may never show — count them at half. */
const CONDITIONAL_DISPLAY_FACTOR = 0.5;
/** Fixed per-section cost: read the intro, hit submit. */
const FORM_SECTION_OVERHEAD_SECONDS = 6;
/** Default weight for a sensagram — its true length is not knowable statically
 * (agenda/wrap-criteria arrive only when the run starts), and a voice
 * conversation is among the longest section types. */
export const SENSAGRAM_DEFAULT_SECONDS = 110;

/**
 * Rough estimated seconds to complete a form section, summed over its fields
 * plus a fixed section overhead. Used only for relative bar sizing — not a real
 * duration estimate.
 */
export function estimateFormSectionSeconds(schema: RenderableFormSectionSchema): number {
  let total = FORM_SECTION_OVERHEAD_SECONDS;
  // Defensive: never crash the session over a progress bar if a node arrives
  // without a well-formed field list — just charge the section overhead.
  const fields = Array.isArray(schema?.fields) ? schema.fields : [];
  for (const f of fields) {
    let seconds = FIELD_BASE_SECONDS[f.field_type] ?? FIELD_BASE_SECONDS.single_select;
    const options = (f as { options?: unknown[] }).options;
    if (Array.isArray(options)) {
      seconds += Math.max(0, options.length - 2) * SECONDS_PER_EXTRA_OPTION;
    }
    if (f.inline_prompts && Object.keys(f.inline_prompts).length > 0) {
      seconds += INLINE_PROMPT_SECONDS;
    }
    if (f.conditional_display) seconds *= CONDITIONAL_DISPLAY_FACTOR;
    total += seconds;
  }
  return total;
}

/**
 * Estimated seconds for any composition node, dispatched by type:
 *  - form_section → {@link estimateFormSectionSeconds}
 *  - sensagram_attachment → {@link SENSAGRAM_DEFAULT_SECONDS}
 *  - interstitials → 0 (not member-facing sections; never on the rail).
 */
export function sectionWeightSeconds(node: RuntimeExperienceNodeV2): number {
  if (node.node_type === 'form_section') {
    return estimateFormSectionSeconds(node.config);
  }
  if (node.node_type === 'sensagram_attachment') {
    return SENSAGRAM_DEFAULT_SECONDS;
  }
  return 0;
}

/**
 * Derive the overall-survey stepper segments from the composition snapshot and
 * the position of the interstitial node the member is currently on.
 *
 * Segments are the member-facing section nodes, ordered by `position`. Sections
 * before `activePosition` are `done`; the first section after it is `current`
 * (the section this interstitial leads into); the rest are `upcoming`. Pass
 * `complete: true` (wrap-up / completed survey) to mark every segment `done`.
 *
 * Pass `preStart: true` for the pre-first / post-intake welcome state (no active
 * node yet): every segment is `upcoming` so nothing reads as entered before the
 * member has left the welcome screen. This is distinct from an interstitial that
 * *leads into* the first section (which has a real position and marks it
 * `current`); `preStart` means no node is active at all. `complete` takes
 * precedence over `preStart`.
 *
 * Returns an empty array when there are no section nodes. Callers should
 * suppress the stepper for a single-section survey (segments.length <= 1).
 */
export function deriveSurveySegments(
  nodes: readonly RuntimeExperienceNodeV2[] | null | undefined,
  activePosition: number | null,
  opts?: { complete?: boolean; preStart?: boolean },
): SurveySegment[] {
  if (!nodes || nodes.length === 0) return [];

  const sections = nodes
    .filter((node) => SECTION_NODE_TYPES.has(node.node_type))
    .slice()
    .sort((a, b) => a.position - b.position);

  const toSegment = (
    node: RuntimeExperienceNodeV2,
    status: SurveySegmentStatus,
  ): SurveySegment => ({
    nodeKey: node.node_key,
    title: node.title ?? null,
    status,
    weight: sectionWeightSeconds(node),
  });

  if (opts?.complete) {
    return sections.map((node) => toSegment(node, 'done'));
  }

  if (opts?.preStart) {
    return sections.map((node) => toSegment(node, 'upcoming'));
  }

  const pos = activePosition ?? Number.NEGATIVE_INFINITY;
  let currentAssigned = false;

  return sections.map((node) => {
    if (node.position < pos) {
      return toSegment(node, 'done');
    }
    if (!currentAssigned) {
      currentAssigned = true;
      return toSegment(node, 'current');
    }
    return toSegment(node, 'upcoming');
  });
}

export type MarkerPlacement = 'on' | 'before' | 'end';

export interface JourneyMarker {
  /** Index of the section the marker anchors to; -1 before the first section. */
  activeIndex: number;
  placement: MarkerPlacement;
}

/**
 * Where the persistent journey rail's "you are here" marker sits, given the
 * derived segments and whether the active node is itself a section.
 *
 *  - inside a section    → 'on' the current segment (the rail highlights it)
 *  - on an interstitial  → 'before' the current segment's left boundary (the
 *                          section it leads into; not yet highlighted)
 *  - completed survey    → 'end', resting past the last segment
 *  - pre-first / no node  → activeIndex -1, 'before' the first segment (welcome)
 *
 * `complete` takes precedence (wrap-up). Otherwise the anchor is the `current`
 * segment from {@link deriveSurveySegments}; `isInSection` (derive from the
 * active node via {@link isSectionNode}) distinguishes "on" (the member is in
 * this section) from "before" (they are on the interstitial that leads into it).
 * A sliding marker needs exactly one anchor every render — this provides it.
 */
export function deriveJourneyMarker(
  segments: readonly SurveySegment[],
  opts: { isInSection: boolean; complete?: boolean },
): JourneyMarker {
  if (opts.complete) {
    return { activeIndex: Math.max(0, segments.length - 1), placement: 'end' };
  }
  const currentIndex = segments.findIndex((s) => s.status === 'current');
  if (currentIndex < 0) {
    return { activeIndex: -1, placement: 'before' };
  }
  return {
    activeIndex: currentIndex,
    placement: opts.isInSection ? 'on' : 'before',
  };
}

export interface ClusteredFieldState {
  /** Whether the field must be answered for the section to be submittable. */
  required: boolean;
  /** Whether the field's current value satisfies its completion rule. */
  complete: boolean;
}

export interface ClusteredCompletion {
  answered: number;
  total: number;
  /** 0..1 completion fraction over the denominator. 0 when the denominator is 0. */
  fraction: number;
}

/**
 * Completion meter for a clustered form, where every field is on screen at once
 * so there is no "position" — only how much of the required work is done.
 *
 * Denominator is the required fields. When a section has no required fields,
 * it falls back to all fields so the meter still moves as the member answers.
 */
export function clusteredCompletion(
  fields: readonly ClusteredFieldState[],
): ClusteredCompletion {
  const required = fields.filter((field) => field.required);
  const pool = required.length > 0 ? required : fields;
  const total = pool.length;
  const answered = pool.filter((field) => field.complete).length;
  const fraction = total > 0 ? answered / total : 0;
  return { answered, total, fraction };
}

export interface SensagramProgressInput {
  display_position?: number | null;
  display_total?: number | null;
  exchange_number?: number | null;
  exchange_count_completed?: number | null;
  estimated_total_exchanges?: number | null;
}

export interface SectionLineProgress {
  /** 0..1 fraction. 0 when indeterminate. */
  fraction: number;
  /** True when no reliable total exists — render a quiet shimmer, not a fill. */
  indeterminate: boolean;
}

// ── Sensagram in-section progress model ──────────────────────────────────────
// A sensagram is an open-ended voice conversation: the engine wraps when the
// topics are covered, somewhere between min_exchanges and max_exchanges. The old
// model divided the CURRENT exchange by the CAP (max_exchanges, or agenda×2),
// which under-reports for the whole run and then snaps to 100% at completion —
// and a separate plumbing bug froze the backend display_position at 1, pinning
// the bar at 1/N the entire run. The honest model below:
//   - counts COMPLETED exchanges (plus a half-credit for the in-flight one),
//   - over an EXPECTED count derived from the version (not the worst-case cap),
//   - rising linearly to EXPECTED_MARK at the estimate, then a saturating tail
//     toward CEILING for overruns — monotonic, never snapping backward, and
//     never hitting 1.0 until the run actually completes (an honest end-jump).

/** Average follow-ups per agenda topic (one pass + some probing). */
const EXPECTED_FOLLOWUP_RATE = 0.5;
/** Rough voice round-trip per exchange, for converting a target duration. */
const SECONDS_PER_EXCHANGE = 30;
/** Fraction shown when completed exchanges reach the expected count. */
const SENSAGRAM_EXPECTED_MARK = 0.85;
/** Hard cap on the fill until the run truly completes (honest end-jump to 1). */
const SENSAGRAM_PROGRESS_CEILING = 0.97;

/**
 * Estimate the EXPECTED number of exchanges for a sensagram from its version
 * snapshot — the denominator for the in-section bar. Agenda-driven (one exchange
 * per topic plus a partial follow-up), optionally blended with a target-duration
 * estimate, then bounded by any explicit wrap criteria. Returns 0 when there is
 * no usable signal (caller falls back or renders indeterminate).
 *
 * This is the EXPECTED length, deliberately NOT `max_exchanges` (a worst-case
 * cap): using the cap as the denominator is exactly what made the bar read low
 * for the whole run.
 */
export function estimateExpectedExchanges(
  agendaLength: number,
  wrap: SensagramWrapCriteria | null | undefined,
): number {
  const topics = Math.max(0, Math.floor(agendaLength));
  let expected = topics > 0 ? topics * (1 + EXPECTED_FOLLOWUP_RATE) : 0;

  const target = wrap?.target_duration_seconds;
  if (typeof target === 'number' && target > 0) {
    const fromDuration = target / SECONDS_PER_EXCHANGE;
    expected = expected > 0 ? (expected + fromDuration) / 2 : fromDuration;
  }

  const min = wrap?.min_exchanges_before_wrap;
  if (typeof min === 'number' && min > 0) expected = Math.max(expected, min);
  const max = wrap?.max_exchanges;
  if (typeof max === 'number' && max > 0) expected = Math.min(expected, max);

  return expected >= 1 ? expected : 0;
}

export interface SensagramProgressOptions {
  /**
   * Expected exchange count (from {@link estimateExpectedExchanges} on the run's
   * version snapshot). When absent, falls back to the backend estimate on the
   * progress object.
   */
  expectedExchanges?: number | null;
  /** The run has completed — show a full, determinate bar. */
  complete?: boolean;
}

/**
 * Monotonic progress over an *estimated* total. Linear to EXPECTED_MARK at the
 * estimate, then a shrinking tail toward CEILING for each exchange past it —
 * smooth, never decreasing, and always below 1.0 (completion is signalled
 * separately). Continuous at the boundary: both branches yield EXPECTED_MARK at
 * position === expected.
 */
function saturatingProgress(position: number, expected: number): number {
  if (position <= 0) return 0;
  const ratio = position / expected;
  if (ratio <= 1) return SENSAGRAM_EXPECTED_MARK * ratio;
  const overrun = position - expected;
  return (
    SENSAGRAM_PROGRESS_CEILING -
    (SENSAGRAM_PROGRESS_CEILING - SENSAGRAM_EXPECTED_MARK) / (1 + overrun)
  );
}

/**
 * In-section progress for a sensagram run. See the model notes above.
 *
 * `complete` short-circuits to a full bar. Otherwise the denominator is the
 * version-derived `expectedExchanges` (preferred), else the backend
 * `estimated_total_exchanges`/`display_total` estimate; with no usable total the
 * bar is indeterminate (a sensagram's length is often unknown up front and a
 * fake position would mislead).
 */
export function sensagramSectionProgress(
  progress: SensagramProgressInput | null | undefined,
  opts?: SensagramProgressOptions,
): SectionLineProgress {
  if (opts?.complete) return { fraction: 1, indeterminate: false };
  if (!progress) return { fraction: 0, indeterminate: true };

  const expected =
    typeof opts?.expectedExchanges === 'number' && opts.expectedExchanges > 0
      ? opts.expectedExchanges
      : typeof progress.estimated_total_exchanges === 'number' &&
          progress.estimated_total_exchanges > 0
        ? progress.estimated_total_exchanges
        : typeof progress.display_total === 'number' && progress.display_total > 0
          ? progress.display_total
          : 0;
  if (expected <= 0) return { fraction: 0, indeterminate: true };

  // Completed exchanges, plus half-credit for the question currently in flight
  // (exchange_number runs one ahead of completed) so the bar isn't pinned at 0
  // while the member is actively answering the first question.
  const completed = Math.max(0, progress.exchange_count_completed ?? 0);
  const exchangeNumber = Math.max(completed, progress.exchange_number ?? completed);
  const inFlight = exchangeNumber > completed ? 0.5 : 0;
  const position = completed + inFlight;

  return { fraction: saturatingProgress(position, expected), indeterminate: false };
}
